import React from 'react'
// Components
import BillingInfoTable from '../BillingInfoTable'
import Panel from '../Panel'

// Local Components
import { CurrentPlan } from '../../components/CurrentPlan'
import ProjectedTotal from '../ProjectedTotal/ProjectedTotal'
import BillingCycle from '../../components/BillingCycle'
import { Button, LinkButton } from '@sketch/components'
import { routes } from '@sketch/modules-common'

import { useToast } from '@sketch/toasts'
// GQL
import {
  BillingPlanFragment,
  BillingSeatsInfoFragment,
  PaymentDetailsFragment,
  GetCustomerDocument,
  GetWorkspaceSettingsDocument,
  useReactivateSubscriptionMutation,
} from '@sketch/gql-types'

// Styles
import {
  CanceledPill,
  BlockText,
  StyledPanelTitle,
} from './PlanOverviewPanel.styles'
import BillingFor from '../BillingFor'

interface PlanOverviewPanelProps {
  workspaceId: string
  customerId: string
  subscriptionEnd?: string
  currentPlan?: BillingPlanFragment
  seats: BillingSeatsInfoFragment
  nextBillingCycleDate?: string
  projectedCost?: number
  isWorkspaceSubscriptionActive: boolean
  isWorkspaceOnTrial: boolean
  isPartner?: boolean
  isSsoEnabled?: boolean
  customerHasPaymentDetails: boolean
  trialEndDate: string
  paymentDetails?: PaymentDetailsFragment
  onWorkspaceSettingRefresh: () => Promise<void>
  onReactivateSubscription: () => void
  onCancelSeatScheduleChange: () => void
  onSeatScheduleChange: () => void
}

/**
 * PlanOverviewPanel
 *
 * This component holds all the data logic needed to fulfill {@link PlanOverviewPanel}.
 */
export const PlanOverviewPanel: React.FC<PlanOverviewPanelProps> = props => {
  const {
    workspaceId,
    customerId,
    isWorkspaceSubscriptionActive,
    isWorkspaceOnTrial,
    customerHasPaymentDetails,
    currentPlan,
    subscriptionEnd,
    seats,
    isPartner,
    isSsoEnabled,
    nextBillingCycleDate,
    projectedCost,
    trialEndDate,
    paymentDetails,
    onWorkspaceSettingRefresh,
    onReactivateSubscription,
    onCancelSeatScheduleChange,
    onSeatScheduleChange,
  } = props
  const { showToast } = useToast()
  const [reactivate, { loading }] = useReactivateSubscriptionMutation({
    onCompleted: () => showToast('Subscription reactivated successfully'),
    variables: { customerId },
    onError: 'show-toast',
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetWorkspaceSettingsDocument,
        variables: {
          identifier: workspaceId,
          includeShares: false,
        },
      },
      {
        query: GetCustomerDocument,
        variables: {
          customerId,
        },
      },
    ],
  })
  const isNewPricingEnabled = currentPlan?.product !== 'BUSINESS'
  const isSubscriptionEnd = !!subscriptionEnd

  const isEducationPlan = currentPlan?.product === 'STUDENT'

  if (!isWorkspaceSubscriptionActive) {
    return (
      <Panel>
        <StyledPanelTitle>Plan Overview</StyledPanelTitle>
        <Panel.Body>
          <BillingInfoTable.Row>
            <BillingInfoTable.HeadingCell>
              Current Plan
            </BillingInfoTable.HeadingCell>
            <BillingInfoTable.Cell>
              <div>
                <CanceledPill data-testid="canceled-subscription-badge">
                  Subscription Canceled
                </CanceledPill>
                <BlockText>
                  Your Workspace subscription has been canceled and documents
                  are now in read-only mode.
                </BlockText>
              </div>
              {isPartner && (
                <Button
                  variant="primary"
                  size="32"
                  loading={loading}
                  onClick={() => reactivate()}
                >
                  Renew
                </Button>
              )}
              {!isPartner && !isSsoEnabled && (
                <LinkButton
                  to={routes.WORKSPACE_SUBSCRIBE.create({
                    workspaceId,
                  })}
                  variant="primary"
                  size="32"
                >
                  Subscribe Again
                </LinkButton>
              )}
            </BillingInfoTable.Cell>
          </BillingInfoTable.Row>
        </Panel.Body>
      </Panel>
    )
  }

  return (
    <Panel>
      <StyledPanelTitle>Plan Overview</StyledPanelTitle>
      <Panel.Body>
        <BillingInfoTable>
          <CurrentPlan
            workspaceId={workspaceId}
            customerId={customerId}
            isWorkspaceOnTrial={isWorkspaceOnTrial}
            customerHasPaymentDetails={customerHasPaymentDetails}
            currentPlan={currentPlan}
            trialEndDate={trialEndDate}
            isSubscriptionEnd={isSubscriptionEnd}
            onWorkspaceSettingRefresh={onWorkspaceSettingRefresh}
            isNewPricingEnabled={isNewPricingEnabled}
            onReactivateSubscription={onReactivateSubscription}
            seats={seats}
            paymentDetails={paymentDetails}
            renewalDate={nextBillingCycleDate!}
            isPartner={isPartner}
          />
          <BillingFor
            seats={seats}
            isSubscriptionEnd={isSubscriptionEnd}
            onSeatScheduleChange={onSeatScheduleChange}
            isEducationPlan={isEducationPlan}
          />
          <ProjectedTotal
            projectedCost={projectedCost}
            seats={seats}
            hasSubscription={customerHasPaymentDetails}
          />
          <BillingCycle
            nextBillingCycleDate={nextBillingCycleDate}
            subscriptionEnd={subscriptionEnd}
            isPartner={isPartner}
            seats={seats}
            isWorkspaceOnTrial={isWorkspaceOnTrial}
            customerHasPaymentDetails={customerHasPaymentDetails}
            onCancelSeatScheduleChange={onCancelSeatScheduleChange}
            currentPlan={currentPlan}
          />
        </BillingInfoTable>
      </Panel.Body>
    </Panel>
  )
}
